<template>
  <div class="app-container">
    <h1>{{ listType === 'offers' ? 'Oferty' : 'Zapytania „Szukam nieruchomości”' }}</h1>

    <!--
      Przełącznik źródła danych. Ogłoszenia (`offers`) i zapytania kupujących
      (`property_requests`) to DWIE różne encje - dlatego obie gałęzie tabeli
      siedzą pod osobnym `v-if`, a nie w jednej tabeli z warunkowymi kolumnami.
      Inaczej ktoś prędzej czy później wywoła na zapytaniu akcję ofertową
      (zmiana statusu po slugu, pakiet, płatność), której backend dla zapytań
      w ogóle nie ma.
    -->
    <div class="filter-container">
      <span>Typ: </span>
      <el-select v-model="listType" class="filter-item" style="width: 240px;" @change="handleListTypeChange">
        <el-option label="Ogłoszenia" value="offers" />
        <el-option :label="requestTypeOptionLabel" value="requests" />
      </el-select>
      <el-tag v-if="pendingRequestsCount > 0" type="warning" class="filter-item" style="margin-left: 10px;">
        Oczekujące zapytania: {{ pendingRequestsCount }}
      </el-tag>
    </div>

    <div v-if="listType === 'offers'" class="filter-container">
      <el-input v-model="query.search" style="width: 200px;" class="filter-item" />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
        {{ $t('table.search') }}
      </el-button>
    </div>
    <div v-if="listType === 'offers'" class="filter-container">
    <span>
        filter: 
      </span>
      <el-select
        v-model="current_agent"
        class="mr-5"
        filterable
        clearable
        placeholder="Wybierz tagi dla swojego artykułu"
        @change="searchOfferList()"
      >
        <el-option
          v-for="agent in agents"
          :key="agent.id"
          :label="agent.name"
          :value="agent.value"
        >
          {{ agent.name }}
        </el-option>
      </el-select>
      <span>
        sort: 
      </span>
      <el-select
        v-model="changeOrder"
        class="location-input"
        filterable
        placeholder="Wybierz opcje sortowania"
        clearable
        @change="changeOfferList"
      >
        <el-option
          v-for="loc in sortOptions"
          :key="loc.id"
          :label="loc.name"
          :value="loc.value"
        />
      </el-select>
    </div>
<!-- 
    <div class="filter-container">
      <span>Użytkownik: </span>
      <el-input
        v-model="userEmailFilter"
        style="width: 250px;"
        class="filter-item"
        placeholder="Email użytkownika"
        clearable
        @clear="handleFilter"
        @keyup.enter.native="handleFilter"
      />
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" size="small" @click="handleFilter">
        Szukaj
      </el-button>
    </div> -->

    <div v-if="listType === 'offers' && selectedOffers.length > 0" class="bulk-actions">
      <el-alert
        :title="'Zaznaczono ' + selectedOffers.length + ' ogłoszeń'"
        type="info"
        :closable="false"
        show-icon
        style="display: inline-flex; margin-right: 10px;"
      />
      <el-button
        type="success"
        size="small"
        icon="el-icon-check"
        :loading="bulkActivating"
        @click="handleBulkActivate"
      >
        Aktywuj zaznaczone
      </el-button>
      <el-button size="small" @click="clearSelection">Odznacz wszystkie</el-button>
    </div>

    <el-table v-if="listType === 'offers'" v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%" :row-class-name="rowClass" @selection-change="handleSelectionChange" ref="offerTable">
      <el-table-column type="selection" width="45" />
      <el-table-column align="center" label="ID" width="80">
        <template slot-scope="scope">
          <span>
            <a :href="frontendUrl + '/ogloszenia/' + scope.row.slug" target="_blank">{{ scope.row.id }}</a>
          </span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Nazwa">
        <template slot-scope="scope">
          <span>{{ scope.row.title }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Cena">
        <template slot-scope="scope">
          <span>{{ scope.row.price / 100 }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Status">
        <template slot-scope="scope">
          <!--
            Wygasła oferta = prawda z `expire_time` (backend liczy `effective_status`),
            a nie sama kolumna `status` - tę przestawia dopiero godzinny cron `ads:status`,
            więc świeżo wygasłe ogłoszenie świeciło tu "Aktywny". Zamiast listy statusów
            (wybór innego statusu i tak nie przedłużyłby `expire_time`) dajemy jedną
            jawną akcję "Aktywuj" - backend nadaje przy niej nowy termin ważności.
          -->
          <div v-if="isExpired(scope.row)">
            <el-tag type="danger">
              Wygasło
            </el-tag>
            <el-button
              v-permission="['manage offer']"
              type="success"
              size="mini"
              icon="el-icon-refresh"
              style="margin-left: 5px;"
              @click="reactivateExpired(scope.row)"
            >
              Aktywuj
            </el-button>
          </div>
          <el-select v-else v-model="scope.row.status" @change="changeStatus(scope.row.slug, scope.row.status)">
            <el-option
              label="Nieaktywny"
              value="in_active"
            />
            <el-option
              label="Dezaktywowane przez użytkownika"
              value="in_active_by_user"
            />
            <el-option
              label="Aktywacja czasowa"
              value="in_active_date"
              v-if="scope.row.visible_from_date !== null"
            />
            <el-option
              label="Aktywny"
              value="active"
              v-if="scope.row.visible_from_date === null"
            />
            <el-option
              label="Odrzucony"
              value="rejected"
            />
            <el-option
              disabled
              label="Wygasło"
              value="expired"
            />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Package">
        <template slot-scope="scope">
          <el-select v-model="scope.row.subscription" value-key="scope.row.subscription" @change="changePackage(scope.row.slug, scope.row.subscription)">
            <el-option v-for="item in outlet" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Właściciel" class="item">
        <template slot-scope="scope">
          <span>{{ scope.row.user.email }}</span>
          <br/>
          <span v-if="scope.row.user.verified === null" class="notify-badge">unverified</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Dodano">
        <template slot-scope="scope">
          <span>{{ scope.row.created_at }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Wygasa">
        <template slot-scope="scope">
          <span>{{ scope.row.expire_time }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Zaktualizowano">
        <template slot-scope="scope">
          <span>{{ scope.row.updated_at }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" label="Akcja" width="450">
        <template slot-scope="scope">
          <el-button v-permission="['manage offer']" type="warning" size="small" icon="el-icon-edit" @click="edit(scope.row.slug)">
            Edytuj
          </el-button>
          <el-button 
            v-if="scope.row.status !== 'active'" 
            v-permission="['manage offer']" 
            type="success" 
            size="small" 
            icon="el-icon-money" 
            @click="showBillingDialog(scope.row)"
          >
            Płać i aktywuj
          </el-button>
          <el-button v-permission="['manage offer']" type="danger" size="small" icon="el-icon-delete" @click="handleDelete(scope.row.slug, scope.row.title);">
            Usuń
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination v-if="listType === 'offers'" v-show="total>0" :total="total" :page.sync="query.page" :limit.sync="query.limit" @pagination="getList" />

    <!-- ===== Gałąź „Szukam": zapytania kupujących (encja `property_requests`) ===== -->
    <template v-if="listType === 'requests'">
      <div class="filter-container">
        <el-input
          v-model="requestQuery.search"
          style="width: 250px;"
          class="filter-item"
          placeholder="Tytuł lub opis"
          clearable
          @clear="handleRequestFilter"
          @keyup.enter.native="handleRequestFilter"
        />
        <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleRequestFilter">
          {{ $t('table.search') }}
        </el-button>
      </div>
      <div class="filter-container">
        <span>moderacja: </span>
        <el-select
          v-model="requestQuery.moderation_status"
          class="filter-item mr-5"
          clearable
          placeholder="Wszystkie"
          @change="handleRequestFilter"
        >
          <el-option v-for="item in moderationStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <span>status kupującego: </span>
        <el-select
          v-model="requestQuery.status"
          class="filter-item mr-5"
          clearable
          placeholder="Wszystkie"
          @change="handleRequestFilter"
        >
          <el-option v-for="item in requestStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <span>sort: </span>
        <el-select v-model="requestSort" class="filter-item" @change="handleRequestSort">
          <el-option v-for="item in requestSortOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </div>

      <!--
        Jawny `key` (tabela ofert go nie ma, więc vnode'y nigdy nie są „te same"):
        bez niego Vue reużywa tej samej instancji `el-table` przy przełączeniu
        typu i tabela zapytań dziedziczy kolumny ofert - m.in. kolumnę
        zaznaczania, bo kolumny rejestrują się w rodzicu i sama podmiana `v-if`
        ich nie czyści.
      -->
      <el-table key="requests-table" v-loading="requestLoading" :data="requestList" border fit highlight-current-row style="width: 100%">
        <el-table-column align="center" label="ID" width="90">
          <template slot-scope="scope">
            <!-- Klucz zapytania to UUID - pełny nie mieści się w kolumnie, więc skrót + tooltip. -->
            <el-tooltip :content="scope.row.id" placement="top">
              <span>{{ shortId(scope.row.id) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Tytuł" min-width="150">
          <template slot-scope="scope">
            <span>{{ scope.row.title }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Budżet" width="145">
          <template slot-scope="scope">
            <!-- `budget_*` są w GROSZACH; do wyświetlania bierzemy gotowe `_pln`. -->
            <span>{{ formatBudget(scope.row) }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Typ transakcji" width="95">
          <template slot-scope="scope">
            <span>{{ transactionTypeLabel(scope.row.transaction_type) }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Moderacja" width="115">
          <template slot-scope="scope">
            <el-tag :type="moderationTagType(scope.row.moderation_status)">
              {{ moderationStatusLabel(scope.row.moderation_status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Status kupującego" width="125">
          <template slot-scope="scope">
            <!-- Druga, NIEZALEŻNA oś: pauzę/„już znalazłem" ustawia kupujący, nie admin. -->
            <el-tag :type="requestStatusTagType(scope.row.status)" size="small">
              {{ requestStatusLabel(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Dopasowania" width="90">
          <template slot-scope="scope">
            <span>{{ scope.row.matches_count }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Właściciel" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.user">
              {{ scope.row.user.name || '-' }}
              <br>
              <small>{{ scope.row.user.email }}</small>
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Dodano" width="105">
          <template slot-scope="scope">
            <span>{{ formatDateTime(scope.row.created_at) }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Wygasa" width="105">
          <template slot-scope="scope">
            <span>{{ formatDateTime(scope.row.expires_at) }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" label="Akcja" width="280">
          <template slot-scope="scope">
            <el-button type="info" size="mini" icon="el-icon-view" @click="showRequestDetails(scope.row)">
              Podgląd
            </el-button>
            <el-button
              v-if="scope.row.moderation_status !== 'approved'"
              v-permission="['manage offer']"
              type="success"
              size="mini"
              icon="el-icon-check"
              @click="approveRequest(scope.row)"
            >
              Akceptuj
            </el-button>
            <el-button
              v-if="scope.row.moderation_status !== 'rejected'"
              v-permission="['manage offer']"
              type="danger"
              size="mini"
              icon="el-icon-close"
              @click="openRejectDialog(scope.row)"
            >
              Odrzuć
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="requestTotal > 0"
        :total="requestTotal"
        :page.sync="requestQuery.page"
        :limit.sync="requestQuery.per_page"
        @pagination="getRequestList"
      />
    </template>

    <!-- Odrzucenie zapytania: powód jest wymagany (trafia do maila kupującego). -->
    <el-dialog
      title="Odrzucenie zapytania"
      :visible.sync="rejectDialogVisible"
      width="40%"
    >
      <p v-if="rejectTarget" style="margin-top: 0;">
        <strong>{{ rejectTarget.title }}</strong>
      </p>
      <el-input
        v-model="rejectReason"
        type="textarea"
        :rows="4"
        maxlength="2000"
        show-word-limit
        placeholder="Powód odrzucenia - zobaczy go kupujący"
      />
      <!--
        Błąd zostaje W DIALOGU i nie kasuje wpisanego tekstu: admin ma poprawić
        powód, a nie pisać go od nowa po każdym 422 z API.
      -->
      <el-alert
        v-if="rejectError"
        :title="rejectError"
        type="error"
        show-icon
        :closable="false"
        style="margin-top: 10px;"
      />
      <span slot="footer">
        <el-button @click="closeRejectDialog">Anuluj</el-button>
        <el-button type="danger" :loading="moderating" @click="confirmReject">Odrzuć</el-button>
      </span>
    </el-dialog>

    <!-- Podgląd zapytania: kryteria, obszary, właściciel, ślad decyzji moderacji. -->
    <el-dialog
      :visible.sync="requestDetailsVisible"
      :title="requestDetails ? requestDetails.title : 'Zapytanie'"
      width="60%"
    >
      <div v-loading="requestDetailsLoading">
        <template v-if="requestDetails">
          <el-alert
            v-if="requestDetails.moderation_status === 'rejected'"
            :title="'Odrzucone ' + formatDateTime(requestDetails.moderated_at)"
            :description="requestDetails.rejection_reason || 'Bez podanego powodu.'"
            type="error"
            show-icon
            :closable="false"
            style="margin-bottom: 15px;"
          />
          <!--
            Powód idzie propem `description`, a nie slotem: `el-alert` ma tylko
            slot `title` i domyślny - `slot="description"` wypada bez śladu
            (treść po prostu nie renderuje się w ogóle).
          -->
          <el-alert
            v-else-if="requestDetails.moderation_status === 'approved'"
            :title="'Zaakceptowane ' + formatDateTime(requestDetails.moderated_at)"
            type="success"
            show-icon
            :closable="false"
            style="margin-bottom: 15px;"
          />
          <el-alert
            v-else
            title="Oczekuje na moderację - strona publiczna zapytania jeszcze nie działa"
            type="warning"
            show-icon
            :closable="false"
            style="margin-bottom: 15px;"
          />

          <h4>Kryteria</h4>
          <table class="request-details-table">
            <tr>
              <th>Typ transakcji</th>
              <td>{{ transactionTypeLabel(requestDetails.transaction_type) }}</td>
            </tr>
            <tr>
              <th>Budżet</th>
              <td>{{ formatBudget(requestDetails) }}</td>
            </tr>
            <tr>
              <th>Metraż</th>
              <td>{{ formatRange(requestDetails.area_min, requestDetails.area_max, ' m²') }}</td>
            </tr>
            <tr>
              <th>Pokoje</th>
              <td>{{ formatRange(requestDetails.rooms_min, requestDetails.rooms_max, '') }}</td>
            </tr>
            <tr>
              <th>Rynek</th>
              <td>{{ marketLabel(requestDetails.market) }}</td>
            </tr>
            <tr>
              <th>Cechy</th>
              <td>{{ formatList(requestDetails.features) }}</td>
            </tr>
            <tr>
              <th>Wykluczenia</th>
              <td>{{ formatList(requestDetails.excluded) }}</td>
            </tr>
            <tr>
              <th>Dopasowania</th>
              <td>{{ requestDetails.matches_count }}</td>
            </tr>
          </table>

          <h4>Obszary</h4>
          <p v-if="!requestDetails.locations || requestDetails.locations.length === 0">Brak wskazanych obszarów.</p>
          <ul v-else class="request-details-list">
            <li v-for="loc in requestDetails.locations" :key="loc.id">
              {{ loc.name }} (+{{ loc.radius_km }} km)
            </li>
          </ul>

          <h4>Opis</h4>
          <p class="request-details-description">{{ requestDetails.description || 'Brak opisu.' }}</p>

          <h4>Właściciel</h4>
          <table class="request-details-table">
            <tr>
              <th>Imię i nazwisko</th>
              <td>{{ (requestDetails.user && requestDetails.user.name) || '-' }}</td>
            </tr>
            <tr>
              <th>E-mail</th>
              <td>{{ (requestDetails.user && requestDetails.user.email) || '-' }}</td>
            </tr>
            <tr>
              <th>Telefon</th>
              <td>{{ (requestDetails.user && requestDetails.user.phone) || '-' }}</td>
            </tr>
            <tr>
              <th>Status kupującego</th>
              <td>{{ requestStatusLabel(requestDetails.status) }}</td>
            </tr>
            <tr>
              <th>Wygasa</th>
              <td>{{ formatDateTime(requestDetails.expires_at) }}</td>
            </tr>
            <tr v-if="requestDetails.moderated_by">
              <th>Moderował (UUID)</th>
              <td><small>{{ requestDetails.moderated_by }}</small></td>
            </tr>
          </table>

          <h4>Strona publiczna</h4>
          <!--
            Link pokazujemy TYLKO dla zaakceptowanych - do decyzji admina
            `/szukam/{slug}` zwraca 404, więc klikalny link byłby kłamstwem.
          -->
          <p v-if="requestDetails.moderation_status === 'approved'">
            <a :href="frontendUrl + '/szukam/' + requestDetails.slug" target="_blank">
              {{ frontendUrl }}/szukam/{{ requestDetails.slug }}
            </a>
          </p>
          <p v-else>
            Niedostępna do czasu akceptacji (<code>/szukam/{{ requestDetails.slug }}</code>).
          </p>
        </template>
      </div>
      <span slot="footer">
        <el-button @click="requestDetailsVisible = false">Zamknij</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="Notatka"
      :visible.sync="dialogVisible"
      width="30%"
      :before-close="closeDialog"
    >
      <el-input v-model="form.note" type="textarea" />
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Anuluj</el-button>
        <el-button type="primary" @click="changeStatusWithNote()">Zapisz</el-button>
      </span>
    </el-dialog>
    <el-dialog :visible.sync="dialogDeleteVisible" :title="'delete ' + offer_title">
      <div v-if="offer_title" v-loading="dialogDeleteLoading" class="form-container">
        <div class="permissions-container">
          <div class="block">
            <el-checkbox
              v-model="delayedDeletion"
            >  delete after six months
            </el-checkbox>
          </div>
          <div class="clear-left" />
        </div>
        <div style="text-align:right;">
          <el-button type="danger" @click="dialogDeleteVisible=false">
            Anuluj
          </el-button>
          <el-button type="primary" @click="confirmDeletion">
            Zapisz
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Billing and Payment Dialog -->
    <el-dialog 
      :visible.sync="billingDialogVisible" 
      :title="'Płatności za ofertę: ' + (selectedOffer ? selectedOffer.title : '')"
      width="60%"
    >
      <div v-loading="billingLoading">
        <div v-if="billingInfo && billingInfo.billAmount > 0">
          <el-alert
            title="Ta oferta wymaga opłacenia funkcji premium"
            type="warning"
            show-icon
            :closable="false"
            style="margin-bottom: 20px;"
          >
            <!-- Treść idzie SLOTEM DOMYŚLNYM, nie `slot="description"`: `el-alert`
                 ma tylko sloty `title` i domyślny, więc nazwany „description"
                 nigdy się nie renderował i kwota do zapłaty w ogóle nie była
                 widoczna. Propa `description` użyć tu nie można - przyjmuje
                 String, a chcemy pogrubioną kwotę. -->
            Całkowita kwota do zapłaty: <strong>{{ billingInfo.billAmount / 100 }} PLN</strong>
          </el-alert>
          
          <h4>Szczegóły płatności:</h4>
          <el-table :data="billingDetails" border>
            <el-table-column prop="name" label="Funkcja" />
            <el-table-column prop="qty" label="Ilość" width="80" />
            <el-table-column label="Cena" width="100">
              <template slot-scope="scope">
                {{ scope.row.value / 100 }} PLN
              </template>
            </el-table-column>
          </el-table>
          
          <div style="margin-top: 20px;">
            <el-checkbox v-model="confirmPayment">
              Potwierdzam, że chcę oznaczyć te funkcje jako opłacone i aktywować ofertę
            </el-checkbox>
          </div>
        </div>
        
        <div v-else-if="billingInfo && billingInfo.billAmount === 0">
          <el-alert
            title="Ta oferta nie wymaga dodatkowych płatności"
            type="success"
            show-icon
            :closable="false"
          >
            <!-- Jak wyżej: slot domyślny, bo `slot="description"` w tej wersji
                 Element UI nie istnieje i komunikat nie był w ogóle widoczny. -->
            Oferta może być aktywowana bezpośrednio bez dodatkowych kosztów.
          </el-alert>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeBillingDialog">Anuluj</el-button>
        <el-button 
          v-if="billingInfo && billingInfo.billAmount > 0"
          type="primary" 
          :disabled="!confirmPayment"
          :loading="activatingWithPayment"
          @click="activateWithPayment"
        >
          Oznacz jako opłacone i aktywuj
        </el-button>
        <el-button 
          v-else-if="billingInfo && billingInfo.billAmount === 0"
          type="primary"
          :loading="activatingWithPayment"
          @click="activateWithPayment"
        >
          Aktywuj ofertę
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'; // Secondary package based on el-pagination
import OfferResource from '@/api/offer';
import PropertyRequestResource from '@/api/propertyRequest';
import waves from '@/directive/waves'; // Waves directive
import permission from '@/directive/permission'; // Permission directive

const offerResource = new OfferResource();
const propertyRequestResource = new PropertyRequestResource();

export default {
  name: 'OfferList',
  components: { Pagination },
  directives: { waves, permission },
  data() {
    return {
      // `total` i `loading` były USTAWIANE w `getList()`, ale nigdy tu
      // zadeklarowane - Vue logowało „Property or method 'total' is not defined",
      // a oba pola nie były reaktywne. Paginacja mimo to działała PRZYPADKIEM:
      // `this.list = data` (pole reaktywne) planuje przerysowanie, które trafia
      // już po przypisaniu `this.total`. Odwrócenie kolejności tych dwóch linii
      // albo ustawienie `total` bez zmiany listy chowałoby paginację bez śladu
      // w kodzie szablonu - stąd jawna deklaracja.
      total: 0,
      loading: false,
      offer_slug: '',
      offer_title: '',
      changeOrder: 'DESC',
      current_agent: 'all',
      sortOptions: [
        {
          id: 1,
          name: 'Najnowsze',
          value: 'DESC'
        },
        {
          id: 2,
          name: 'Najstarsze',
          value: 'ASC'
        },
        {
          id: 3,
          name: 'recently updated',
          value: 'update'
        },
      ],
      agents: [
        {
          id: 1,
          name: 'all',
          value: 'all'
        },
        {
          id: 2,
          name: 'active',
          value: 'active'

        },
        {
          id: 3,
          name: 'deactivated',
          value: 'deactivated'

        },
        {
          id: 4,
          name: 'inactive',
          value: 'inactive'

        },
        {
          id: 5,
          name: 'rejected',
          value: 'rejected'

        },
        {
          id: 6,
          name: 'expired',
          value: 'expired'

        },
      ],
      dialogVisible: false,
      dialogDeleteLoading: false,
      dialogDeleteVisible: false,
      delayedDeletion: false,
      form: {
        note: '',
        slug: '',
        status: '',
      },
      packages:{
        1:'Darmowe',
        2:'Standard',
        3:'Silver',
        4:'Gold'
      },
      outlet: [
        { value: 1, label: 'Darmowe', slug: 1 },
        { value: 2, label: 'Standard', slug: 2 },
        { value: 3, label: 'Silver', slug: 3 },
        { value: 4, label: 'Gold', slug: 4 },
      ],
      query: {
        page: 1,
        limit: 15,
        order: 'DESC',
        order_by: 'created_at',
        filterUI: 'all'
      },
      list: null,
      billingDialogVisible: false,
      billingLoading: false,
      billingInfo: null,
      selectedOffer: null,
      confirmPayment: false,
      activatingWithPayment: false,
      userEmailFilter: '',
      selectedOffers: [],
      bulkActivating: false,

      // --- Gałąź „Szukam" (zapytania kupujących) ---
      // Stan trzymany ODDZIELNIE od stanu ofert (`query`, `list`, `total`),
      // żeby przełączenie typu nie przenosiło filtrów ani wierszy między
      // encjami, które nie mają wspólnych kolumn.
      listType: 'offers',
      requestList: [],
      requestTotal: 0,
      requestLoading: false,
      pendingRequestsCount: 0,
      requestQuery: {
        page: 1,
        per_page: 15,
        search: '',
        moderation_status: '',
        status: '',
        order_by: 'created_at',
        order: 'DESC',
      },
      requestSort: 'created_at:DESC',
      requestSortOptions: [
        { value: 'created_at:DESC', label: 'Najnowsze' },
        { value: 'created_at:ASC', label: 'Najstarsze' },
        { value: 'updated_at:DESC', label: 'Ostatnio zmienione' },
        { value: 'title:ASC', label: 'Tytuł A-Z' },
        { value: 'moderation_status:ASC', label: 'Status moderacji' },
      ],
      moderationStatusOptions: [
        { value: 'pending', label: 'Oczekujące' },
        { value: 'approved', label: 'Zaakceptowane' },
        { value: 'rejected', label: 'Odrzucone' },
      ],
      requestStatusOptions: [
        { value: 'active', label: 'Aktywne' },
        { value: 'paused', label: 'Wstrzymane' },
        { value: 'fulfilled', label: 'Zrealizowane' },
        { value: 'expired', label: 'Wygasłe' },
      ],
      moderating: false,
      rejectDialogVisible: false,
      rejectTarget: null,
      rejectReason: '',
      rejectError: '',
      requestDetailsVisible: false,
      requestDetailsLoading: false,
      requestDetails: null,
    };
  },
  computed: {
    frontendUrl() {
      return process.env.VUE_APP_FRONTEND_URL;
    },
    billingDetails() {
      if (!this.billingInfo || !this.billingInfo.details) {
        return [];
      }
      return Object.values(this.billingInfo.details);
    },
    /**
     * Licznik w etykiecie opcji, a nie tylko obok selecta: przy wybranym
     * typie „Ogłoszenia" rozwinięta lista jest jedynym miejscem, w którym
     * admin zobaczy, że coś czeka na decyzję.
     */
    requestTypeOptionLabel() {
      return this.pendingRequestsCount > 0
        ? 'Szukam (' + this.pendingRequestsCount + ' oczekujących)'
        : 'Szukam';
    },
  },
  created() {
    this.getList();
    this.refreshPendingRequestsCount();
  },
  methods: {
    changeOfferList () {
      if(this.changeOrder == 'update'){
        this.query.order = 'DESC'
        this.query.order_by = 'updated_at'
      }else{
        this.query.order = this.changeOrder
        this.query.order_by = 'created_at'
      }
      this.getList()
    },
    searchOfferList () {
      // if (this.searchTxt) {
      //   this.query.keyword = this.searchTxt
      // }
      // if (this.changeOrder) {
      //   this.query.sort = this.changeOrder
      // }
      if (this.current_agent) {
        this.query.filterUI = this.current_agent
      }
      this.getList()
    },
    isExpired(row) {
      // Wyłącznie `effective_status` - backend podmienia status na `expired` TYLKO dla
      // surowego `active` (OfferExtendedResource). Fallback na `is_expired` (czyste
      // `expire_time < now`) oznaczałby oferty odrzucone/oczekujące z minionym terminem
      // jako "Wygasło" bez dropdownu statusu, a "Aktywuj" mógłby przywrócić odrzuconą.
      return row.effective_status === 'expired';
    },
    rowClass({ row, rowIndex }) {
      if (row.visible_from_date !== null && row.status === 'active') {
        return 'warning-row';
      }
    },
    closeDialog() {
      this.dialogVisible = false;
    },
    edit(slug) {
      this.$router.push({ path: '/offers/edit?slug=' + slug });
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    async handleDelete(slug, title) {
      this.dialogDeleteVisible = true;
      this.dialogDeleteLoading = true;
      this.offer_title = title;
      this.offer_slug = slug;
      this.dialogDeleteLoading = false;
    },
    confirmDeletion(){
      if(this.delayedDeletion){
        offerResource.delayedDelete(this.offer_slug).then(response => {
          this.$message({
            type: 'success',
            message: 'ta oferta zostanie usunięta po 6 miesiącach',
          });
          this.handleFilter();
        }).catch(error => {
          console.log(error);
        });
      }else{
        offerResource.destroy(this.offer_slug).then(response => {
          this.$message({
            type: 'success',
            message: 'Poprawnie usunięto użytkownika',
          });
          this.handleFilter();
        }).catch(error => {
          console.log(error);
        });
      }
      this.dialogDeleteVisible = false;

    },
    async getList() {
      this.loading = true;
      const queryParams = { ...this.query };
      if (this.userEmailFilter) {
        queryParams.user_email = this.userEmailFilter;
      }
      const { data, total } = await offerResource.list(queryParams);
      this.list = data;
      this.total = total;
      this.loading = false;
    },
    async changeStatusWithNote() {
      await offerResource.changeStatus(this.form.slug, this.form.status, this.form.note);
      this.dialogVisible = false;
      this.$notify({
        title: 'Sukces',
        message: 'Status ogłoszenia został zmieniony',
        type: 'success',
      });
    },
    async changeStatus(slug, status) {
      if (status === 'rejected') {
        this.dialogVisible = true;
        this.form.status = status;
        this.form.slug = slug;
        return;
      }
      await offerResource.changeStatus(slug, status, null);
      this.$notify({
        title: 'Sukces',
        message: 'Status ogłoszenia został zmieniony',
        type: 'success',
      });
    },
    async reactivateExpired(row) {
      // Ponowna aktywacja wygasłej oferty: backend (Admin\OfferController::changeStatus)
      // przy statusie `active` i minionym `expire_time` nadaje nowy termin ważności
      // (długość pakietu "Darmowe"), więc oferta faktycznie wraca do obiegu,
      // a nie wraca na listę od razu jako wygasła. Komunikat nie podaje liczby dni,
      // bo długość jest konfigurowana w pakiecie, nie zaszyta w panelu.
      try {
        await this.$confirm(
          'Aktywować ponownie wygasłą ofertę? Ogłoszenie dostanie nowy termin ważności.',
          'Potwierdzenie aktywacji',
          {
            confirmButtonText: 'Aktywuj',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch {
        return; // user cancelled
      }

      await this.changeStatus(row.slug, 'active');
      this.getList();
    },
    async changePackage(slug, status) {
      await offerResource.changePackage(slug, status, null);
      this.$notify({
        title: 'Sukces',
        message: 'Pakiet został zmieniony',
        type: 'success',
      });
    },
    async deleteRecord(slug) {
      console.log(slug)
      await offerResource.destroy(slug);
      this.getList();
    },
    deleteCancel () {
      console.log('delete cancel')
    },
    async showBillingDialog(offer) {
      this.selectedOffer = offer;
      this.billingDialogVisible = true;
      this.billingLoading = true;
      this.confirmPayment = false;
      
      console.log('Fetching billing info for offer:', offer.slug);
      
      try {
        const response = await offerResource.getBillingInfo(offer.slug);
        console.log('Billing response:', response);
        this.billingInfo = response;
        console.log('Billing info set:', this.billingInfo);
      } catch (error) {
        console.error('Billing API error:', error);
        this.$message.error('Błąd podczas pobierania informacji o płatnościach');
      } finally {
        this.billingLoading = false;
      }
    },
    closeBillingDialog() {
      this.billingDialogVisible = false;
      this.selectedOffer = null;
      this.billingInfo = null;
      this.confirmPayment = false;
    },
    async activateWithPayment() {
      if (!this.selectedOffer) return;
      
      this.activatingWithPayment = true;
      
      try {
        await offerResource.activateWithPayment(this.selectedOffer.slug);
        
        this.$message.success('Oferta została aktywowana z opłaconymi funkcjami premium');
        
        // Update the offer status in the list
        const offerIndex = this.list.findIndex(o => o.slug === this.selectedOffer.slug);
        if (offerIndex !== -1) {
          this.list[offerIndex].status = 'active';
        }
        
        this.closeBillingDialog();
      } catch (error) {
        this.$message.error('Błąd podczas aktywacji oferty');
        console.error(error);
      } finally {
        this.activatingWithPayment = false;
      }
    },
    handleSelectionChange(selection) {
      this.selectedOffers = selection;
    },
    clearSelection() {
      this.$refs.offerTable.clearSelection();
      this.selectedOffers = [];
    },
    async handleBulkActivate() {
      if (this.selectedOffers.length === 0) return;

      try {
        await this.$confirm(
          `Czy na pewno chcesz aktywować ${this.selectedOffers.length} zaznaczonych ogłoszeń?`,
          'Potwierdzenie aktywacji',
          {
            confirmButtonText: 'Aktywuj',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch {
        return; // user cancelled
      }

      this.bulkActivating = true;

      try {
        const slugs = this.selectedOffers.map(o => o.slug);
        await offerResource.bulkActivate(slugs);

        this.$message.success(`Aktywowano ${slugs.length} ogłoszeń`);
        this.clearSelection();
        this.getList();
      } catch (error) {
        this.$message.error('Błąd podczas masowej aktywacji');
        console.error(error);
      } finally {
        this.bulkActivating = false;
      }
    },

    // ===== Gałąź „Szukam": zapytania kupujących =====

    handleListTypeChange() {
      if (this.listType === 'requests') {
        // Tabela ofert znika przez `v-if`, ale `selectedOffers` przeżyłoby
        // przełączenie: po powrocie pasek zbiorczy pokazywałby „Zaznaczono N",
        // choć w nowej instancji tabeli żaden checkbox nie jest zaznaczony -
        // i masowa akcja poszłaby na oferty, których admin już nie widzi.
        // `clearSelection()` tu nie zadziała: `$refs.offerTable` właśnie znika.
        this.selectedOffers = [];
        this.requestQuery.page = 1;
        this.getRequestList();
        this.refreshPendingRequestsCount();
      } else {
        this.getList();
      }
    },

    /** Filtry i sortowanie idą do API - lista jest paginowana po stronie backendu. */
    buildRequestParams() {
      const params = {
        page: this.requestQuery.page,
        per_page: this.requestQuery.per_page,
        order_by: this.requestQuery.order_by,
        order: this.requestQuery.order,
      };
      ['search', 'moderation_status', 'status'].forEach(key => {
        if (this.requestQuery[key]) {
          params[key] = this.requestQuery[key];
        }
      });
      return params;
    },

    async getRequestList() {
      this.requestLoading = true;
      try {
        const response = await propertyRequestResource.list(this.buildRequestParams());
        this.requestList = response.data || [];
        this.requestTotal = (response.meta && response.meta.total) || 0;
      } catch (error) {
        console.error(error);
      } finally {
        this.requestLoading = false;
      }
    },

    handleRequestFilter() {
      this.requestQuery.page = 1;
      this.getRequestList();
    },

    handleRequestSort(value) {
      const parts = String(value).split(':');
      this.requestQuery.order_by = parts[0];
      this.requestQuery.order = parts[1] || 'DESC';
      this.handleRequestFilter();
    },

    async refreshPendingRequestsCount() {
      // Bez uprawnienia do listy zapytanie i tak wróciłoby 403, a globalny
      // interceptor pokazałby adminowi błąd, którego nie sprowokował.
      const permissions = (this.$store.getters && this.$store.getters.permissions) || [];
      if (!permissions.includes('admin offers index')) {
        return;
      }
      try {
        const response = await propertyRequestResource.pendingCount();
        this.pendingRequestsCount = (response.meta && response.meta.total) || 0;
      } catch (error) {
        console.error(error);
      }
    },

    async approveRequest(row) {
      try {
        await this.$confirm(
          'Zaakceptować zapytanie „' + row.title + '"? Trafi na publiczną listę, agenci zobaczą je w swoich dopasowaniach, a kupujący zacznie dostawać powiadomienia o nowych ofertach.',
          'Potwierdzenie akceptacji',
          {
            confirmButtonText: 'Akceptuj',
            cancelButtonText: 'Anuluj',
            type: 'warning',
          }
        );
      } catch {
        return; // admin zrezygnował
      }

      this.moderating = true;
      try {
        const updated = await propertyRequestResource.moderate(row.id, 'approved', null);
        this.applyModerationResult(updated);
        this.$notify({
          title: 'Sukces',
          message: 'Zapytanie zostało zaakceptowane',
          type: 'success',
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.moderating = false;
      }
    },

    openRejectDialog(row) {
      this.rejectTarget = row;
      // Świadomie startujemy od poprzedniego powodu - najczęstsze odrzucenie
      // po akceptacji to powtórka wcześniejszej decyzji.
      this.rejectReason = row.rejection_reason || '';
      this.rejectError = '';
      this.rejectDialogVisible = true;
    },

    closeRejectDialog() {
      this.rejectDialogVisible = false;
      this.rejectTarget = null;
      this.rejectReason = '';
      this.rejectError = '';
    },

    async confirmReject() {
      if (!this.rejectTarget) {
        return;
      }
      const reason = (this.rejectReason || '').trim();
      if (!reason) {
        this.rejectError = 'Powód odrzucenia jest wymagany - zobaczy go kupujący.';
        return;
      }
      if (reason.length > 2000) {
        this.rejectError = 'Powód odrzucenia nie może przekraczać 2000 znaków.';
        return;
      }

      this.moderating = true;
      this.rejectError = '';
      try {
        const updated = await propertyRequestResource.moderate(this.rejectTarget.id, 'rejected', reason);
        this.applyModerationResult(updated);
        this.closeRejectDialog();
        this.$notify({
          title: 'Sukces',
          message: 'Zapytanie zostało odrzucone',
          type: 'success',
        });
      } catch (error) {
        // Dialog ZOSTAJE otwarty z wpisanym tekstem - inaczej admin pisałby
        // powód od nowa po każdym 422 z API.
        this.rejectError = this.extractApiError(error, 'Nie udało się odrzucić zapytania.');
      } finally {
        this.moderating = false;
      }
    },

    /** Komunikat walidacji z odpowiedzi API (`{ errors: { pole: [...] } }`). */
    extractApiError(error, fallback) {
      const data = error && error.response && error.response.data;
      if (!data) {
        return fallback;
      }
      if (data.errors) {
        const messages = [];
        Object.keys(data.errors).forEach(key => {
          const value = data.errors[key];
          messages.push(Array.isArray(value) ? value.join(' ') : String(value));
        });
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }
      if (data.message) {
        return String(data.message);
      }
      return fallback;
    },

    /**
     * Wiersz podmieniamy odpowiedzią backendu (ma już nowy `moderation_status`,
     * `moderated_at` i przeliczone `matches_count`). Przy włączonym filtrze
     * moderacji przeładowujemy stronę - zmieniony wiersz zwykle przestaje
     * do niego pasować i zostawianie go na liście mylnie sugerowałoby,
     * że filtr go obejmuje.
     */
    applyModerationResult(updated) {
      const payload = updated && updated.data && updated.data.id ? updated.data : updated;
      if (payload && payload.id) {
        const index = this.requestList.findIndex(item => item.id === payload.id);
        if (index !== -1) {
          this.$set(this.requestList, index, payload);
        }
        if (this.requestDetails && this.requestDetails.id === payload.id) {
          this.requestDetails = payload;
        }
      }
      if (this.requestQuery.moderation_status) {
        this.getRequestList();
      }
      this.refreshPendingRequestsCount();
    },

    async showRequestDetails(row) {
      this.requestDetails = row;
      this.requestDetailsVisible = true;
      this.requestDetailsLoading = true;
      try {
        // Szczegóły ciągniemy osobno - lista nie gwarantuje kompletu
        // (obszary, telefon właściciela) po ewentualnej zmianie zakresu pól.
        const response = await propertyRequestResource.get(row.id);
        const payload = response && response.data && response.data.id ? response.data : response;
        if (payload && payload.id) {
          this.requestDetails = payload;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.requestDetailsLoading = false;
      }
    },

    shortId(id) {
      const value = String(id || '');
      return value.length > 8 ? value.slice(0, 8) + '...' : value;
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      return Number(value).toLocaleString('pl-PL') + ' zł';
    },

    /** Widełki budżetu ze złotych (`_pln`) - grosze zostają w `budget_min/max`. */
    formatBudget(row) {
      if (!row) {
        return '-';
      }
      const min = this.formatMoney(row.budget_min_pln);
      const max = this.formatMoney(row.budget_max_pln);
      if (min && max) {
        return min + ' - ' + max;
      }
      if (min) {
        return 'od ' + min;
      }
      if (max) {
        return 'do ' + max;
      }
      return 'bez widełek';
    },

    formatRange(min, max, unit) {
      const hasMin = min !== null && min !== undefined && min !== '';
      const hasMax = max !== null && max !== undefined && max !== '';
      if (hasMin && hasMax) {
        return min + ' - ' + max + unit;
      }
      if (hasMin) {
        return 'od ' + min + unit;
      }
      if (hasMax) {
        return 'do ' + max + unit;
      }
      return 'dowolnie';
    },

    formatList(values) {
      return Array.isArray(values) && values.length > 0 ? values.join(', ') : 'brak';
    },

    formatDateTime(value) {
      if (!value) {
        return '-';
      }
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return String(value);
      }
      const pad = n => String(n).padStart(2, '0');
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) +
        ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
    },

    transactionTypeLabel(value) {
      // Slugi atrybutu 1 - te same, których używa formularz „Szukam".
      const labels = { sell: 'Kupno', rent: 'Wynajem', swap: 'Zamiana' };
      return labels[value] || value || '-';
    },

    marketLabel(value) {
      if (!value) {
        return 'dowolny';
      }
      const normalized = String(value).toLowerCase();
      if (['original', 'pierwotny', 'primary'].includes(normalized)) {
        return 'Rynek pierwotny';
      }
      if (['secondary', 'wtorny', 'wtórny'].includes(normalized)) {
        return 'Rynek wtórny';
      }
      return value;
    },

    moderationStatusLabel(value) {
      const labels = { pending: 'Oczekuje', approved: 'Zaakceptowane', rejected: 'Odrzucone' };
      return labels[value] || value || '-';
    },

    moderationTagType(value) {
      const types = { pending: 'warning', approved: 'success', rejected: 'danger' };
      return types[value] || 'info';
    },

    requestStatusLabel(value) {
      const labels = {
        active: 'Aktywne',
        paused: 'Wstrzymane',
        fulfilled: 'Zrealizowane',
        expired: 'Wygasłe',
      };
      return labels[value] || value || '-';
    },

    requestStatusTagType(value) {
      const types = {
        active: 'success',
        paused: 'warning',
        fulfilled: 'info',
        expired: 'danger',
      };
      return types[value] || 'info';
    },
  },
};
</script>

<style lang="scss">
.item {
    display:inline-block;
}
.notify-badge{
    background:red;
    text-align: center;
    border-radius: 30px 30px 30px 30px;
    color:white;
    font-size:11px;
    padding: 5px;
}
.el-table .warning-row {
  background: oldlace;
}
.bulk-actions {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 8px 0;
}
.request-details-table {
  width: 100%;
  margin-bottom: 15px;
  th {
    text-align: left;
    width: 180px;
    padding: 4px 8px 4px 0;
    color: #606266;
    font-weight: 500;
    vertical-align: top;
  }
  td {
    padding: 4px 0;
  }
}
.request-details-list {
  margin: 0 0 15px;
  padding-left: 20px;
}
.request-details-description {
  white-space: pre-wrap;
  margin-bottom: 15px;
}
.edit-input {
  padding-right: 100px;
}
.cancel-btn {
  position: absolute;
  right: 15px;
  top: 10px;
}
.dialog-footer {
  text-align: left;
  padding-top: 0;
  margin-left: 150px;
}
.app-container {
  flex: 1;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
  .block {
    float: left;
    min-width: 250px;
  }
  .clear-left {
    clear: left;
  }
}
</style>
